import React, { useEffect, useRef, useState, useCallback } from "react";
import { Maximize2 } from "lucide-react";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoiZW5hcnRyYXoiLCJhIjoiY21rcTViYnh6MG90djNlcGxxNmd6dTcwZSJ9.thqCqMAsCvfBfaDdQCYXsg";

// Función SIMPLIFICADA para obtener coordenadas
const obtenerCoordenadas = (prop) => {
  // DEBUG: Ver qué datos tenemos
  console.log("Propiedad recibida:", {
    id: prop.id,
    titulo: prop.titulo,
    lat: prop.lat,
    lng: prop.lng,
    pos_y: prop.pos_y,
    pos_x: prop.pos_x,
    "lat es undefined?": prop.lat === undefined,
    "lng es undefined?": prop.lng === undefined,
    "pos_y es number?": typeof prop.pos_y,
    "pos_x es number?": typeof prop.pos_x,
  });

  // PRIORIDAD 1: Si tenemos pos_y y pos_x (que es tu caso actual)
  if (prop.pos_y !== undefined && prop.pos_x !== undefined) {
    const lat = parseFloat(prop.pos_y);
    const lng = parseFloat(prop.pos_x);

    if (!isNaN(lat) && !isNaN(lng)) {
      console.log(`✅ Usando pos_y/pos_x: ${lat}, ${lng}`);
      return { lat, lng, fuente: "pos_y/pos_x" };
    }
  }

  // PRIORIDAD 2: Si tenemos lat y lng
  if (prop.lat !== undefined && prop.lng !== undefined) {
    const lat = parseFloat(prop.lat);
    const lng = parseFloat(prop.lng);

    if (!isNaN(lat) && !isNaN(lng)) {
      console.log(`✅ Usando lat/lng: ${lat}, ${lng}`);
      return { lat, lng, fuente: "lat/lng" };
    }
  }

  // PRIORIDAD 3: latitude/longitude (por si acaso)
  if (prop.latitude !== undefined && prop.longitude !== undefined) {
    const lat = parseFloat(prop.latitude);
    const lng = parseFloat(prop.longitude);

    if (!isNaN(lat) && !isNaN(lng)) {
      console.log(`✅ Usando latitude/longitude: ${lat}, ${lng}`);
      return { lat, lng, fuente: "latitude/longitude" };
    }
  }

  console.log("❌ No hay coordenadas válidas");
  return null;
};

export default function MapboxPlano({
  propiedades = [],
  onSeleccionarPropiedad,
  propiedadSeleccionada = null,
  propiedadEnfocada = null,
  solicitudCentradoMapa = 0,
}) {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    conCoordenadas: 0,
    sinCoordenadas: 0,
  });

  // Cargar Mapbox GL JS dinámicamente
  const loadMapbox = useCallback(async () => {
    try {
      if (!MAPBOX_TOKEN || MAPBOX_TOKEN.includes("TU_TOKEN")) {
        throw new Error("Token de Mapbox no configurado");
      }

      // Cargar CSS
      if (!document.querySelector('link[href*="mapbox-gl.css"]')) {
        const link = document.createElement("link");
        link.href = "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css";
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }

      // Cargar JS
      if (!window.mapboxgl) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src =
            "https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js";
          script.async = true;

          script.onload = () => {
            window.mapboxgl.accessToken = MAPBOX_TOKEN;
            resolve();
          };

          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
    } catch (err) {
      console.error("Error cargando Mapbox:", err);
      setError(err.message);
    }
  }, []);

  // Inicializar mapa
  const initMap = useCallback(() => {
    try {
      if (!mapContainer.current || !window.mapboxgl) return;

      const map = new window.mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/light-v11",
        center: [-89.592585, 20.96737], // Mérida
        zoom: 12,
        minZoom: 10,
        maxZoom: 18,
        attributionControl: false,
      });

      mapRef.current = map;

      map.on("load", () => {
        console.log("✅ Mapa de Mapbox cargado");
        setMapLoaded(true);
      });

      map.on("click", () => {
        // Cerrar todos los popups al hacer click en el mapa
        markersRef.current.forEach((marker) => {
          if (marker.popup) {
            marker.popup.remove();
            marker.popup = null;
          }
        });
      });

      map.on("error", (e) => {
        console.error("Error en mapa:", e);
        setError(e.error?.message || "Error del mapa");
      });

      // Controles
      map.addControl(new window.mapboxgl.NavigationControl(), "top-right");
      map.addControl(new window.mapboxgl.FullscreenControl(), "top-right");
      map.addControl(
        new window.mapboxgl.ScaleControl({ unit: "metric" }),
        "bottom-left",
      );
    } catch (err) {
      console.error("Error inicializando mapa:", err);
      setError(err.message);
    }
  }, []);

  const getPropertyId = useCallback((prop) => prop?.id || prop?._id, []);

  const isSelectedProperty = useCallback(
    (prop) => {
      if (!propiedadSeleccionada) return false;

      const selectedId = getPropertyId(propiedadSeleccionada);
      const currentId = getPropertyId(prop);

      if (selectedId && currentId) return selectedId === currentId;

      return (
        propiedadSeleccionada.titulo === prop.titulo &&
        propiedadSeleccionada.ubicacion === prop.ubicacion
      );
    },
    [getPropertyId, propiedadSeleccionada],
  );

  // Añadir marcadores
  const addMarkers = useCallback(
    (map) => {
      if (!map || !propiedades.length) return;

      console.log("=== INICIANDO PROCESO DE MARCADORES ===");
      console.log("Total propiedades recibidas:", propiedades.length);

      // Limpiar marcadores anteriores
      markersRef.current.forEach((marker) => {
        if (marker.popup) marker.popup.remove();
        if (marker.marker) marker.marker.remove();
      });
      markersRef.current = [];

      // Procesar propiedades
      const propiedadesConCoords = [];
      const propiedadesSinCoords = [];

      propiedades.forEach((prop) => {
        const coords = obtenerCoordenadas(prop);

        if (coords) {
          propiedadesConCoords.push({
            ...prop,
            _coordenadas: coords,
          });
        } else {
          propiedadesSinCoords.push(prop);
        }
      });

      console.log(
        `✅ Propiedades con coordenadas: ${propiedadesConCoords.length}`,
      );
      console.log(
        `⚠ Propiedades sin coordenadas: ${propiedadesSinCoords.length}`,
      );

      // Mostrar ejemplos
      if (propiedadesConCoords.length > 0) {
        console.log("Ejemplo de coordenadas encontradas:", {
          titulo: propiedadesConCoords[0].titulo,
          lat: propiedadesConCoords[0]._coordenadas.lat,
          lng: propiedadesConCoords[0]._coordenadas.lng,
          fuente: propiedadesConCoords[0]._coordenadas.fuente,
        });
      }

      // Actualizar estadísticas
      setEstadisticas({
        total: propiedades.length,
        conCoordenadas: propiedadesConCoords.length,
        sinCoordenadas: propiedadesSinCoords.length,
      });

      if (propiedadesConCoords.length === 0) {
        console.warn("No hay propiedades con coordenadas para mostrar");
        return;
      }

      // Agrupar propiedades (menos agrupación, más precisión)
      console.log(
        "TIPO OPERACION DEBUG:",
        propiedadesConCoords.map((p) => ({
          id: p.id,
          titulo: p.titulo,
          tipo_operacion: p.tipo_operacion,
        })),
      );

      const grupos = {};
      const precision = 10000; // 4 decimales (~11 metros)

      propiedadesConCoords.forEach((prop) => {
        const latRounded =
          Math.round(prop._coordenadas.lat * precision) / precision;
        const lngRounded =
          Math.round(prop._coordenadas.lng * precision) / precision;
        const key = `${latRounded.toFixed(4)},${lngRounded.toFixed(4)}`;

        if (!grupos[key]) {
          grupos[key] = {
            coordenadas: [prop._coordenadas.lng, prop._coordenadas.lat], // [lng, lat] para Mapbox
            propiedades: [],
            color: prop.tipo_operacion === "venta" ? "#10b981" : "#3b82f6",
          };
        }
        grupos[key].propiedades.push(prop);
      });

      console.log(`📍 Grupos creados: ${Object.keys(grupos).length}`);

      // Crear marcadores
      Object.values(grupos).forEach((grupo) => {
        const isCluster = grupo.propiedades.length > 1;

        // Crear elemento HTML del marcador
        const el = document.createElement("div");
        el.className = "mapbox-marker";

        if (isSelectedProperty(grupo.propiedades[0])) {
          el.style.visibility = "hidden";
          el.style.pointerEvents = "none";
        }

        if (isCluster) {
          // Marcador de cluster
          const size = Math.max(
            30,
            Math.min(24 + grupo.propiedades.length * 4, 60),
          );
          el.innerHTML = `
          <div class="marker-cluster" style="
            width: ${size}px;
            height: ${size}px;
            background: ${grupo.color}80;
            border: 3px solid ${grupo.color};
            border-radius: 50%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            transition: transform 0.2s;
          ">
            <span style="color: white; font-weight: bold; font-size: ${Math.min(14, 10 + grupo.propiedades.length)}px;">
              ${grupo.propiedades.length}
            </span>
          </div>
        `;
        } else {
          // Marcador individual
          el.innerHTML = `
          <div class="marker-single" style="
            width: 36px;
            height: 36px;
            background: ${grupo.color};
            border: 3px solid white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            box-shadow: 0 3px 10px rgba(0,0,0,0.3);
            transition: transform 0.2s;
          ">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </div>
        `;
        }

        // Eventos hover
        const innerDiv =
          el.querySelector(".marker-cluster") ||
          el.querySelector(".marker-single");
        if (innerDiv) {
          innerDiv.addEventListener("mouseenter", () => {
            innerDiv.style.transform = "scale(1.15)";
          });
          innerDiv.addEventListener("mouseleave", () => {
            innerDiv.style.transform = "scale(1)";
          });
        }

        // Crear marcador
        const marker = new window.mapboxgl.Marker({
          element: el,
          anchor: "bottom",
        })
          .setLngLat(grupo.coordenadas)
          .addTo(map);

        const markerEntry = { marker, popup: null, grupo };
        markersRef.current.push(markerEntry);

        // Evento click
        el.addEventListener("click", (e) => {
          e.stopPropagation();

          // Cerrar otros popups
          markersRef.current.forEach((m) => {
            if (m.marker !== marker && m.popup) {
              m.popup.remove();
              m.popup = null;
            }
          });

          if (markerEntry.popup) {
            markerEntry.popup.remove();
            markerEntry.popup = null;
          }

          if (isCluster) {
            // Zoom al cluster
            map.flyTo({
              center: grupo.coordenadas,
              zoom: Math.min(map.getZoom() + 2, 16),
              duration: 800,
            });

            // Mostrar popup
            const popup = new window.mapboxgl.Popup({
              offset: 25,
              closeButton: true,
              closeOnClick: false,
              maxWidth: "300px",
            })
              .setLngLat(grupo.coordenadas)
              .setHTML(
                `
              <div style="padding: 12px;">
                <h4 style="margin: 0 0 10px 0; font-weight: bold; color: #1f2937; font-size: 14px;">
                  ${grupo.propiedades.length} propiedades en esta zona
                </h4>
                <div style="max-height: 200px; overflow-y: auto; font-size: 12px;">
                  ${grupo.propiedades
                    .map(
                      (prop) => `
                    <div class="property-item" data-id="${getPropertyId(prop)}" style="
                      padding: 8px;
                      margin-bottom: 6px;
                      background: #f8fafc;
                      border-radius: 6px;
                      border-left: 3px solid ${prop.tipo_operacion === "venta" ? "#10b981" : "#3b82f6"};
                      cursor: pointer;
                    ">
                      <div style="font-weight: 500; color: #374151;">${prop.titulo || "Propiedad"}</div>
                      <div style="color: #3b82f6; font-weight: bold;">$${prop.precio?.toLocaleString() || "Consultar"}</div>
                    </div>
                  `,
                    )
                    .join("")}
                </div>
              </div>
            `,
              )
              .addTo(map);

            markerEntry.popup = popup;

            // Agregar event listeners
            setTimeout(() => {
              document.querySelectorAll(".property-item").forEach((item) => {
                item.addEventListener("click", () => {
                  const propId = item.getAttribute("data-id");
                  const propiedad = propiedades.find(
                    (p) => String(getPropertyId(p)) === propId,
                  );

                  if (propiedad) {
                    popup.remove();
                    markerEntry.popup = null;
                    onSeleccionarPropiedad?.(propiedad);
                  }
                });
              });
            }, 50);

            return;
          }

          // Propiedad individual
          const prop = grupo.propiedades[0];
          const propertyId = String(getPropertyId(prop));
          const popup = new window.mapboxgl.Popup({
            offset: 25,
            closeButton: true,
            closeOnClick: false,
            maxWidth: "250px",
          })
            .setLngLat(grupo.coordenadas)
            .setHTML(
              `
            <div style="padding: 12px;">
              <h4 style="margin: 0 0 8px 0; font-weight: bold; color: #1f2937;">
                ${prop.titulo || "Propiedad"}
              </h4>
              <p style="margin: 0 0 8px 0; color: #3b82f6; font-size: 18px; font-weight: bold;">
                $${prop.precio?.toLocaleString() || "Consultar"}
              </p>
              <p style="margin: 0 0 12px 0; color: #6b7280; font-size: 14px;">
                ${prop.ubicacion || "Mérida, Yucatán"}
              </p>
              <div style="display: flex; gap: 16px; margin-bottom: 12px;">
                <span style="display: flex; align-items: center; gap: 4px; color: #4b5563;">
                  🛏 ${prop.caracteristicas?.recamaras || 0}
                </span>
                <span style="display: flex; align-items: center; gap: 4px; color: #4b5563;">
                  🚿 ${prop.caracteristicas?.banos || 0}
                </span>
              </div>
              <button class="view-details-btn" data-id="${propertyId}"
                style="width: 100%; padding: 8px 16px; background: #3b82f6; color: white; border: none; border-radius: 6px; cursor: pointer; font-weight: 500;">
                Ver detalles
              </button>
            </div>
          `,
            )
            .addTo(map);

          markerEntry.popup = popup;

          // Agregar event listener
          setTimeout(() => {
            const btn = document.querySelector(
              `.view-details-btn[data-id="${propertyId}"]`,
            );

            if (btn) {
              btn.addEventListener("click", () => {
                popup.remove();
                markerEntry.popup = null;
                onSeleccionarPropiedad?.(prop);
              });
            }
          }, 50);

          onSeleccionarPropiedad?.(prop);
        });
      });

      console.log("=== PROCESO DE MARCADORES COMPLETADO ===");
    },
    [getPropertyId, isSelectedProperty, onSeleccionarPropiedad, propiedades],
  );

  // Efecto principal
  useEffect(() => {
    // Cargar e inicializar mapa
    loadMapbox().then(() => {
      if (window.mapboxgl) {
        setTimeout(initMap, 100);
      }
    });

    return () => {
      // Limpiar marcadores
      markersRef.current.forEach((marker) => {
        if (marker.popup) marker.popup.remove();
        if (marker.marker) marker.marker.remove();
      });
      markersRef.current = [];

      // Limpiar mapa
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [loadMapbox, initMap]);

  // Actualizar marcadores cuando cambian las propiedades
  useEffect(() => {
    if (mapLoaded && mapRef.current) {
      console.log("🔄 Actualizando marcadores...");
      addMarkers(mapRef.current);
    }
  }, [propiedades, mapLoaded, addMarkers]);

  // Centrar mapa
  const centerMap = () => {
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [-89.592585, 20.96737],
        zoom: 12,
        duration: 1000,
      });
    }
  };

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;

    if (!propiedadSeleccionada) return;

    const coords = obtenerCoordenadas(propiedadSeleccionada);
    if (!coords) return;

    const el = document.createElement("div");
    el.className = "selected-map-marker";
    el.innerHTML = `
      <div style="transform: translateY(-8px); filter: drop-shadow(0 8px 16px rgba(239,68,68,0.35));">
        <style>
          @keyframes selectedPulseOnce {
            0% { transform: scale(0.7); opacity: 0.45; }
            70% { transform: scale(1.7); opacity: 0.12; }
            100% { transform: scale(1.9); opacity: 0; }
          }
        </style>
        <div style="position: relative; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;">
          <span style="position: absolute; inset: 0; border-radius: 9999px; background: rgba(239,68,68,0.18); animation: selectedPulseOnce 1.1s cubic-bezier(0, 0, 0.2, 1) 1 forwards;"></span>
          <span style="position: absolute; width: 24px; height: 24px; border-radius: 9999px; background: rgba(255,255,255,0.55);"></span>
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="position: relative; z-index: 1;">
            <path d="M12 22s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" fill="#ef4444"/>
            <circle cx="12" cy="11" r="2.2" fill="white"/>
          </svg>
        </div>
      </div>
    `;

    const selectedMarker = new window.mapboxgl.Marker({
      element: el,
      anchor: "bottom",
    })
      .setLngLat([coords.lng, coords.lat])
      .addTo(mapRef.current);

    return () => selectedMarker.remove();
  }, [propiedadSeleccionada, mapLoaded]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !propiedadEnfocada) return;

    const coords = obtenerCoordenadas(propiedadEnfocada);
    if (!coords) return;

    mapRef.current.flyTo({
      center: [coords.lng, coords.lat],
      zoom: 16,
      duration: 1000,
      essential: true,
    });
  }, [propiedadEnfocada, mapLoaded, solicitudCentradoMapa]);

  return (
    <div className="relative w-full h-full">
      {/* Contenedor del mapa */}
      <div
        ref={mapContainer}
        className="absolute inset-0 w-full h-full"
        style={{
          minHeight: "500px",
          backgroundColor: mapLoaded ? "transparent" : "#f8fafc",
        }}
      />
      {/* Estado de carga */}
      {!mapLoaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-700">Cargando mapa...</p>
          </div>
        </div>
      )}
      {/* Error */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-20 p-6">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl text-red-500">!</span>
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              Error con Mapbox
            </h3>
            <p className="text-slate-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Reintentar
            </button>
          </div>
        </div>
      )}
      {mapLoaded && (
        <>
          <div className="absolute bottom-12 right-10 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-slate-200 p-4 z-10 min-w-[200px]">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Leyenda
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                <span className="text-sm">Venta</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                <span className="text-sm">Renta</span>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <div className="text-xs text-slate-500 space-y-1">
                <div className="font-medium text-slate-700">
                  {estadisticas.conCoordenadas}/{estadisticas.total} propiedades
                </div>
                {estadisticas.sinCoordenadas > 0 && (
                  <div className="text-amber-600 text-[10px]">
                    ⚠ {estadisticas.sinCoordenadas} sin coordenadas
                  </div>
                )}
                <div className="text-[10px] mt-1">Haz click para detalles</div>
              </div>
            </div>
          </div>

          <button
            onClick={centerMap}
            className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-xl shadow-lg border border-slate-200 hover:bg-white z-10"
            title="Centrar en Mérida"
          >
            <Maximize2 size={20} className="text-slate-700" />
          </button>
        </>
      )}
    </div>
  );
}
